import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentIndRounded,
  BusinessRounded,
  ErrorOutlineRounded,
  FolderOpenRounded,
  HourglassBottomRounded,
  OpenInNewRounded,
  PlaylistAddCheckRounded,
  QueryStatsRounded,
  TrackChangesRounded,
} from '@mui/icons-material';
import {
  formatOperationalRelativeDay,
  getOperationalDayDifference,
} from './IndustrialWorkflowDatePicker';
import type {
  IndustrialWorkflowAssignableUser,
  IndustrialWorkflowCase,
  IndustrialWorkflowOperationsAreaSummary,
  IndustrialWorkflowOperationsAttentionItem,
  IndustrialWorkflowOperationsDashboard,
  IndustrialWorkflowOperationsFollowUp,
  IndustrialWorkflowOperationsTaskItem,
} from '../types/industrialWorkflows';

type OperationsView =
  | 'summary'
  | 'my-work'
  | 'areas'
  | 'follow-ups';

type IndustrialWorkflowsOperationsDashboardProps = {
  activeView: OperationsView;
  dashboard: IndustrialWorkflowOperationsDashboard | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  assignableUsers: IndustrialWorkflowAssignableUser[];
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
};

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo cargar la información operativa.';
}

function formatRelativeDate(value: string | null) {
  return (
    formatOperationalRelativeDay(value)
    ?? 'sin fecha'
  );
}

function getOperationalDueStatus(
  value: string | null,
) {
  const differenceInDays =
    getOperationalDayDifference(value);

  if (differenceInDays === null) {
    return 'without_due';
  }

  if (differenceInDays < 0) {
    return 'overdue';
  }

  if (differenceInDays === 0) {
    return 'today';
  }

  return 'upcoming';
}

function formatTaskDeadlineLabel(
  value: string | null,
  isExternalWait: boolean,
) {
  const relativeText =
    formatOperationalRelativeDay(value);

  if (!relativeText) {
    return isExternalWait
      ? 'Respuesta · sin fecha'
      : 'Sin fecha';
  }

  if (relativeText.startsWith('hace ')) {
    return isExternalWait
      ? `Respuesta vencida · ${relativeText}`
      : `Vencida · ${relativeText}`;
  }

  if (isExternalWait) {
    return `Respuesta ${relativeText}`;
  }

  if (relativeText === 'hoy') {
    return 'Vence hoy';
  }

  if (relativeText === 'mañana') {
    return 'Vence mañana';
  }

  return `Vence · ${relativeText}`;
}

function formatFollowUpDeadlineLabel(
  value: string | null,
) {
  const relativeText =
    formatOperationalRelativeDay(value);

  if (!relativeText) {
    return 'Sin próxima revisión';
  }

  if (relativeText.startsWith('hace ')) {
    return `Seguimiento vencido · ${relativeText}`;
  }

  return `Seguimiento ${relativeText}`;
}

function getDueTone(
  dueStatus: string,
) {
  if (dueStatus === 'overdue') {
    return {
      color: '#fca5a5',
      bgcolor: alpha('#ef4444', 0.16),
    };
  }

  if (dueStatus === 'today') {
    return {
      color: '#fde68a',
      bgcolor: alpha('#f59e0b', 0.16),
    };
  }

  return {
    color: '#cbd5e1',
    bgcolor: alpha('#64748b', 0.14),
  };
}

function getAssignableUserLabel(
  user: IndustrialWorkflowAssignableUser | undefined,
) {
  return (
    user?.full_name?.trim()
    || user?.email?.trim()
    || 'Sin responsable'
  );
}

function TaskRow({
  task,
  assignableUserById,
  onOpenCase,
}: {
  task: IndustrialWorkflowOperationsTaskItem;
  assignableUserById: Map<string, IndustrialWorkflowAssignableUser>;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  const assignedUser = task.assigned_user_id
    ? assignableUserById.get(task.assigned_user_id)
    : undefined;

  const dueTone = getDueTone(
    getOperationalDueStatus(
      task.due_at,
    ),
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${alpha('#94a3b8', 0.14)}`,
        bgcolor: 'rgba(15,23,42,0.42)',
        px: 1.25,
        py: 1.05,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={0.65}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              {task.title}
            </Typography>

            {task.task_type === 'outside_system_wait' ? (
              <Chip
                size="small"
                label="Espera externa"
                sx={{
                  color: '#fde68a',
                  bgcolor: alpha('#f59e0b', 0.15),
                }}
              />
            ) : null}
          </Stack>

          <Stack
            direction="row"
            spacing={0.65}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.65 }}
          >
            <Chip
              size="small"
              label={task.case.title}
              sx={{
                color: '#bae6fd',
                bgcolor: alpha('#0284c7', 0.15),
              }}
            />

            {task.work_area_name ? (
              <Chip
                size="small"
                label={task.work_area_name}
                sx={{
                  color: '#cbd5e1',
                  bgcolor: alpha('#64748b', 0.12),
                }}
              />
            ) : null}

            <Chip
              size="small"
              label={getAssignableUserLabel(assignedUser)}
              sx={{
                color: '#cbd5e1',
                bgcolor: alpha('#64748b', 0.12),
              }}
            />

            <Chip
              size="small"
              label={formatTaskDeadlineLabel(
                task.due_at,
                task.task_type === 'outside_system_wait',
              )}
              sx={dueTone}
            />
          </Stack>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewRounded />}
          onClick={() => onOpenCase(task.case)}
          sx={{
            fontWeight: 900,
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Abrir expediente
        </Button>
      </Stack>
    </Paper>
  );
}

function TaskSection({
  title,
  subtitle,
  tasks,
  assignableUserById,
  onOpenCase,
}: {
  title: string;
  subtitle: string;
  tasks: IndustrialWorkflowOperationsTaskItem[];
  assignableUserById: Map<string, IndustrialWorkflowAssignableUser>;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha('#38bdf8', 0.14)}`,
        bgcolor: 'rgba(2,6,23,0.18)',
        px: 1.35,
        py: 1.25,
      }}
    >
      <Stack spacing={1}>
        <Box>
          <Typography
            sx={{ color: '#f8fafc', fontWeight: 950 }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: '#94a3b8',
              fontSize: '0.8rem',
              mt: 0.2,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        {tasks.length ? (
          <Stack spacing={0.75}>
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                assignableUserById={assignableUserById}
                onOpenCase={onOpenCase}
              />
            ))}
          </Stack>
        ) : (
          <Typography
            sx={{
              color: '#64748b',
              fontSize: '0.84rem',
            }}
          >
            No hay elementos en esta cola.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

type SummaryIndicatorTone =
  | 'neutral'
  | 'warning'
  | 'critical';

type SummaryAreaTaskGroup = {
  title: string;
  activeTaskCount: number;
  assignedTaskCount: number;
  unassignedTaskCount: number;
  inProgressTaskCount: number;
  overdueTaskCount: number;
  hasExternalWait: boolean;
};

function getSummaryIndicatorTone(
  tone: SummaryIndicatorTone,
) {
  if (tone === 'critical') {
    return {
      accentColor: '#f87171',
      borderColor: alpha('#ef4444', 0.26),
      backgroundColor: alpha('#7f1d1d', 0.12),
      iconBackgroundColor: alpha('#ef4444', 0.16),
    };
  }

  if (tone === 'warning') {
    return {
      accentColor: '#fbbf24',
      borderColor: alpha('#f59e0b', 0.24),
      backgroundColor: alpha('#78350f', 0.1),
      iconBackgroundColor: alpha('#f59e0b', 0.15),
    };
  }

  return {
    accentColor: '#38bdf8',
    borderColor: alpha('#38bdf8', 0.2),
    backgroundColor: 'rgba(15,23,42,0.48)',
    iconBackgroundColor: alpha('#0284c7', 0.16),
  };
}

function getAttentionItemPresentation(
  kind: IndustrialWorkflowOperationsAttentionItem['kind'],
) {
  if (kind === 'task_overdue') {
    return {
      label: 'Tarea vencida',
      color: '#fca5a5',
      bgcolor: alpha('#ef4444', 0.16),
      icon: <HourglassBottomRounded />,
    };
  }

  if (kind === 'stage_overdue') {
    return {
      label: 'Etapa fuera de plazo',
      color: '#fca5a5',
      bgcolor: alpha('#ef4444', 0.16),
      icon: <ErrorOutlineRounded />,
    };
  }

  return {
    label: 'Seguimiento pendiente',
    color: '#fde68a',
    bgcolor: alpha('#f59e0b', 0.16),
    icon: <TrackChangesRounded />,
  };
}

function groupSummaryAreaTasks(
  tasks: IndustrialWorkflowOperationsTaskItem[],
): SummaryAreaTaskGroup[] {
  const groupByTitle = new Map<
    string,
    SummaryAreaTaskGroup
  >();

  tasks.forEach((task) => {
    const title = task.title.trim() || 'Tarea sin título';

    const existingGroup = groupByTitle.get(title) ?? {
      title,
      activeTaskCount: 0,
      assignedTaskCount: 0,
      unassignedTaskCount: 0,
      inProgressTaskCount: 0,
      overdueTaskCount: 0,
      hasExternalWait: false,
    };

    existingGroup.activeTaskCount += 1;

    if (task.assigned_user_id) {
      existingGroup.assignedTaskCount += 1;
    } else {
      existingGroup.unassignedTaskCount += 1;
    }

    if (task.status === 'in_progress') {
      existingGroup.inProgressTaskCount += 1;
    }

    if (task.due_status === 'overdue') {
      existingGroup.overdueTaskCount += 1;
    }

    if (task.task_type === 'outside_system_wait') {
      existingGroup.hasExternalWait = true;
    }

    groupByTitle.set(title, existingGroup);
  });

  return [...groupByTitle.values()].sort(
    (left, right) =>
      right.overdueTaskCount - left.overdueTaskCount
      || right.unassignedTaskCount - left.unassignedTaskCount
      || right.activeTaskCount - left.activeTaskCount
      || left.title.localeCompare(right.title),
  );
}

function SummaryAreaWorkloadCard({
  area,
}: {
  area: IndustrialWorkflowOperationsAreaSummary;
}) {
  const taskGroups = groupSummaryAreaTasks(area.tasks);
  const visibleTaskGroups = taskGroups.slice(0, 5);
  const hiddenTaskGroupCount =
    taskGroups.length - visibleTaskGroups.length;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha('#38bdf8', 0.16)}`,
        bgcolor: 'rgba(15,23,42,0.44)',
        overflow: 'hidden',
      }}
    >
      <Stack
        spacing={1}
        sx={{
          px: 1.35,
          py: 1.25,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={0.9}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
          >
            <Box
              sx={{
                display: 'flex',
                borderRadius: 2,
                color:
                  area.work_area_id === '__without_work_area__'
                    ? '#fbbf24'
                    : '#38bdf8',
                bgcolor:
                  area.work_area_id === '__without_work_area__'
                    ? alpha('#f59e0b', 0.15)
                    : alpha('#0284c7', 0.16),
                p: 0.65,
              }}
            >
              <BusinessRounded
                sx={{ fontSize: '1.05rem' }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  lineHeight: 1.2,
                }}
              >
                {area.name}
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.74rem',
                  mt: 0.16,
                }}
              >
                {area.case_count}{' '}
                {area.case_count === 1
                  ? 'expediente relacionado'
                  : 'expedientes relacionados'}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              label={`${area.active_task_count} activas`}
              sx={{
                color: '#bae6fd',
                bgcolor: alpha('#0284c7', 0.15),
              }}
            />

            <Chip
              size="small"
              label={`${area.active_task_count - area.unassigned_task_count} asignadas`}
              sx={{
                color: '#cbd5e1',
                bgcolor: alpha('#64748b', 0.13),
              }}
            />

            <Chip
              size="small"
              label={`${area.unassigned_task_count} sin asignar`}
              sx={{
                color:
                  area.unassigned_task_count
                    ? '#fde68a'
                    : '#94a3b8',
                bgcolor:
                  area.unassigned_task_count
                    ? alpha('#f59e0b', 0.15)
                    : alpha('#64748b', 0.12),
              }}
            />

            <Chip
              size="small"
              label={`${area.in_progress_task_count} en curso`}
              sx={{
                color: '#c4b5fd',
                bgcolor: alpha('#8b5cf6', 0.15),
              }}
            />

            {area.overdue_task_count ? (
              <Chip
                size="small"
                label={`${area.overdue_task_count} vencidas`}
                sx={{
                  color: '#fca5a5',
                  bgcolor: alpha('#ef4444', 0.16),
                }}
              />
            ) : null}
          </Stack>
        </Stack>

        <Box
          sx={{
            borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
            pt: 0.3,
          }}
        >
          {visibleTaskGroups.map((taskGroup) => (
            <Stack
              key={taskGroup.title}
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={0.65}
              sx={{
                py: 0.72,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.08)}`,
                '&:last-of-type': {
                  borderBottom: 'none',
                },
              }}
            >
              <Stack
                direction="row"
                spacing={0.55}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography
                  sx={{
                    color: '#e2e8f0',
                    fontSize: '0.84rem',
                    fontWeight: 850,
                  }}
                >
                  {taskGroup.title}
                </Typography>

                {taskGroup.hasExternalWait ? (
                  <Chip
                    size="small"
                    label="Espera externa"
                    sx={{
                      height: 20,
                      color: '#fde68a',
                      bgcolor: alpha('#f59e0b', 0.15),
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.68rem',
                      },
                    }}
                  />
                ) : null}
              </Stack>

              <Stack
                direction="row"
                spacing={0.45}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  label={`${taskGroup.activeTaskCount} activas`}
                />

                <Chip
                  size="small"
                  label={`${taskGroup.assignedTaskCount} asignadas`}
                />

                {taskGroup.unassignedTaskCount ? (
                  <Chip
                    size="small"
                    label={`${taskGroup.unassignedTaskCount} sin asignar`}
                    sx={{
                      color: '#fde68a',
                      bgcolor: alpha('#f59e0b', 0.15),
                    }}
                  />
                ) : null}

                {taskGroup.inProgressTaskCount ? (
                  <Chip
                    size="small"
                    label={`${taskGroup.inProgressTaskCount} en curso`}
                    sx={{
                      color: '#c4b5fd',
                      bgcolor: alpha('#8b5cf6', 0.15),
                    }}
                  />
                ) : null}

                {taskGroup.overdueTaskCount ? (
                  <Chip
                    size="small"
                    label={`${taskGroup.overdueTaskCount} vencidas`}
                    sx={{
                      color: '#fca5a5',
                      bgcolor: alpha('#ef4444', 0.16),
                    }}
                  />
                ) : null}
              </Stack>
            </Stack>
          ))}

          {hiddenTaskGroupCount > 0 ? (
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.74rem',
                fontWeight: 800,
                mt: 0.8,
              }}
            >
              {hiddenTaskGroupCount}{' '}
              {hiddenTaskGroupCount === 1
                ? 'tipo de tarea adicional'
                : 'tipos de tarea adicionales'}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function SummaryView({
  dashboard,
  onOpenCase,
}: {
  dashboard: IndustrialWorkflowOperationsDashboard;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  const indicators = [
    {
      label: 'Expedientes activos',
      value: dashboard.summary.active_cases,
      description: 'Volumen abierto del proceso',
      tone: 'neutral',
      icon: <FolderOpenRounded />,
    },
    {
      label: 'Etapas fuera de plazo',
      value: dashboard.summary.overdue_stages,
      description: 'Entradas actuales vencidas',
      tone: 'critical',
      icon: <ErrorOutlineRounded />,
    },
    {
      label: 'Tareas activas',
      value: dashboard.summary.active_tasks,
      description: 'Trabajo pendiente o en curso',
      tone: 'neutral',
      icon: <PlaylistAddCheckRounded />,
    },
    {
      label: 'Tareas sin responsable',
      value: dashboard.summary.unassigned_tasks,
      description: 'Requieren asignación',
      tone: 'warning',
      icon: <AssignmentIndRounded />,
    },
    {
      label: 'Tareas vencidas',
      value: dashboard.summary.overdue_tasks,
      description: 'Requieren intervención',
      tone: 'critical',
      icon: <HourglassBottomRounded />,
    },
    {
      label: 'Seguimientos pendientes',
      value: dashboard.summary.pending_followups,
      description: 'Esperas que deben revisarse',
      tone: 'warning',
      icon: <TrackChangesRounded />,
    },
  ] as const;

  const visibleAttentionItems =
    dashboard.attention_items.slice(0, 6);

  const hiddenAttentionItemCount =
    dashboard.attention_items.length
    - visibleAttentionItems.length;

  const maxStageCaseCount = Math.max(
    ...dashboard.stage_summaries.map(
      (stage) => stage.case_count,
    ),
    1,
  );

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography
          sx={{
            color: '#f8fafc',
            fontWeight: 950,
            fontSize: '1.03rem',
          }}
        >
          Visión operativa
        </Typography>

        <Typography
          sx={{
            color: '#94a3b8',
            fontSize: '0.82rem',
            mt: 0.2,
          }}
        >
          Estado actual del proceso, puntos de atención y distribución
          del trabajo activo.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(6, minmax(0, 1fr))',
          },
          gap: 0.9,
        }}
      >
        {indicators.map((indicator) => {
          const tone = getSummaryIndicatorTone(
            indicator.value
              ? indicator.tone
              : 'neutral',
          );

          return (
            <Paper
              key={indicator.label}
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                minHeight: 112,
                borderRadius: 2.5,
                border: `1px solid ${tone.borderColor}`,
                bgcolor: tone.backgroundColor,
                px: 1.25,
                py: 1.1,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: 3,
                  bgcolor: tone.accentColor,
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexShrink: 0,
                    borderRadius: 2,
                    color: tone.accentColor,
                    bgcolor: tone.iconBackgroundColor,
                    p: 0.7,
                    '& svg': {
                      fontSize: '1.15rem',
                    },
                  }}
                >
                  {indicator.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.66rem',
                      fontWeight: 850,
                      lineHeight: 1.25,
                      textTransform: 'uppercase',
                    }}
                  >
                    {indicator.label}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 950,
                      fontSize: '1.55rem',
                      lineHeight: 1.05,
                      mt: 0.35,
                    }}
                  >
                    {indicator.value}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.72rem',
                      lineHeight: 1.25,
                      mt: 0.4,
                    }}
                  >
                    {indicator.description}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: 'minmax(0, 1.35fr) minmax(320px, 0.65fr)',
          },
          gap: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha('#f59e0b', 0.2)}`,
            bgcolor: 'rgba(2,6,23,0.2)',
            px: 1.35,
            py: 1.25,
          }}
        >
          <Stack spacing={0.9}>
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Requiere atención
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.78rem',
                  mt: 0.2,
                }}
              >
                Expedientes con vencimientos o seguimientos que
                requieren intervención.
              </Typography>
            </Box>

            {visibleAttentionItems.length ? (
              <Stack spacing={0.65}>
                {visibleAttentionItems.map((item) => {
                  const presentation =
                    getAttentionItemPresentation(item.kind);

                  return (
                    <Paper
                      key={`${item.kind}-${item.case.id}-${item.task_id ?? ''}`}
                      elevation={0}
                      sx={{
                        borderRadius: 2.2,
                        border: `1px solid ${alpha('#94a3b8', 0.13)}`,
                        bgcolor: 'rgba(15,23,42,0.46)',
                        px: 1.05,
                        py: 0.85,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={0.8}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={0.55}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                color: presentation.color,
                                '& svg': {
                                  fontSize: '1rem',
                                },
                              }}
                            >
                              {presentation.icon}
                            </Box>

                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                              }}
                            >
                              {item.case.title}
                            </Typography>

                            <Chip
                              size="small"
                              label={item.case.reference_code}
                              sx={{
                                height: 20,
                                color: '#7dd3fc',
                                bgcolor: alpha('#0284c7', 0.14),
                                '& .MuiChip-label': {
                                  px: 0.7,
                                  fontSize: '0.67rem',
                                },
                              }}
                            />
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={0.55}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 0.5 }}
                          >
                            <Chip
                              size="small"
                              label={presentation.label}
                              sx={{
                                color: presentation.color,
                                bgcolor: presentation.bgcolor,
                              }}
                            />

                            <Typography
                              sx={{
                                color: '#cbd5e1',
                                fontSize: '0.8rem',
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Stack>
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewRounded />}
                          onClick={() => onOpenCase(item.case)}
                          sx={{
                            fontWeight: 900,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Abrir expediente
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}

                {hiddenAttentionItemCount > 0 ? (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.74rem',
                      fontWeight: 800,
                    }}
                  >
                    {hiddenAttentionItemCount}{' '}
                    {hiddenAttentionItemCount === 1
                      ? 'incidencia adicional'
                      : 'incidencias adicionales'}
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.84rem',
                }}
              >
                No hay incidencias que requieran atención.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha('#38bdf8', 0.16)}`,
            bgcolor: 'rgba(2,6,23,0.2)',
            px: 1.35,
            py: 1.25,
          }}
        >
          <Stack spacing={0.9}>
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Expedientes por etapa
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.78rem',
                  mt: 0.2,
                }}
              >
                Distribución actual dentro del flujo.
              </Typography>
            </Box>

            {dashboard.stage_summaries.length ? (
              dashboard.stage_summaries.map((stage) => (
                <Box
                  key={stage.stage_definition_id}
                  sx={{
                    pb: 0.72,
                    borderBottom: `1px solid ${alpha('#94a3b8', 0.09)}`,
                    '&:last-of-type': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontSize: '0.82rem',
                        fontWeight: 800,
                      }}
                    >
                      {stage.stage_name}
                    </Typography>

                    <Stack direction="row" spacing={0.45}>
                      <Chip
                        size="small"
                        label={stage.case_count}
                      />

                      {stage.overdue_case_count ? (
                        <Chip
                          size="small"
                          label={`${stage.overdue_case_count} fuera de plazo`}
                          sx={{
                            color: '#fca5a5',
                            bgcolor: alpha('#ef4444', 0.16),
                          }}
                        />
                      ) : null}
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      height: 5,
                      borderRadius: 999,
                      bgcolor: alpha('#64748b', 0.16),
                      overflow: 'hidden',
                      mt: 0.55,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(
                          (stage.case_count / maxStageCaseCount) * 100,
                          7,
                        )}%`,
                        height: 1,
                        borderRadius: 999,
                        bgcolor: stage.overdue_case_count
                          ? '#f87171'
                          : '#38bdf8',
                      }}
                    />
                  </Box>
                </Box>
              ))
            ) : (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.84rem',
                }}
              >
                No hay expedientes activos.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha('#38bdf8', 0.16)}`,
          bgcolor: 'rgba(2,6,23,0.2)',
          px: 1.35,
          py: 1.25,
        }}
      >
        <Stack spacing={1}>
          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
              }}
            >
              Carga de trabajo por área
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.78rem',
                mt: 0.2,
              }}
            >
              Trabajo activo agrupado por área y por tipo de tarea.
              Las esperas externas se identifican de forma separada.
            </Typography>
          </Box>

          {dashboard.area_summaries.length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  xl: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {dashboard.area_summaries.map((area) => (
                <SummaryAreaWorkloadCard
                  key={area.work_area_id}
                  area={area}
                />
              ))}
            </Box>
          ) : (
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.84rem',
              }}
            >
              No hay tareas activas distribuidas por área.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

function MyWorkView({
  dashboard,
  assignableUserById,
  onOpenCase,
}: {
  dashboard: IndustrialWorkflowOperationsDashboard;
  assignableUserById: Map<string, IndustrialWorkflowAssignableUser>;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  return (
    <Stack spacing={1}>
      <TaskSection
        title="Asignadas a mí"
        subtitle="Trabajo reservado o en curso bajo tu responsabilidad."
        tasks={dashboard.my_work.assigned_tasks}
        assignableUserById={assignableUserById}
        onOpenCase={onOpenCase}
      />

      <TaskSection
        title="Disponibles en mis áreas"
        subtitle="Tareas sin responsable que puedes asumir."
        tasks={dashboard.my_work.available_tasks}
        assignableUserById={assignableUserById}
        onOpenCase={onOpenCase}
      />
    </Stack>
  );
}

function AreaCard({
  area,
  assignableUserById,
  onOpenCase,
}: {
  area: IndustrialWorkflowOperationsAreaSummary;
  assignableUserById: Map<string, IndustrialWorkflowAssignableUser>;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha('#38bdf8', 0.14)}`,
        bgcolor: 'rgba(2,6,23,0.18)',
        px: 1.35,
        py: 1.25,
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={0.8}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <BusinessRounded sx={{ color: '#38bdf8' }} />

            <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
              {area.name}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${area.active_task_count} activas`} />
            <Chip size="small" label={`${area.unassigned_task_count} sin responsable`} />
            <Chip size="small" label={`${area.in_progress_task_count} en curso`} />

            {area.overdue_task_count ? (
              <Chip
                size="small"
                label={`${area.overdue_task_count} vencidas`}
                sx={{
                  color: '#fca5a5',
                  bgcolor: alpha('#ef4444', 0.16),
                }}
              />
            ) : null}
          </Stack>
        </Stack>

        {area.tasks.length ? (
          <Stack spacing={0.65}>
            {area.tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                assignableUserById={assignableUserById}
                onOpenCase={onOpenCase}
              />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
            No hay trabajo activo en esta área.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function AreasView({
  dashboard,
  assignableUserById,
  onOpenCase,
}: {
  dashboard: IndustrialWorkflowOperationsDashboard;
  assignableUserById: Map<string, IndustrialWorkflowAssignableUser>;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  return (
    <Stack spacing={1}>
      {dashboard.area_summaries.map((area) => (
        <AreaCard
          key={area.work_area_id}
          area={area}
          assignableUserById={assignableUserById}
          onOpenCase={onOpenCase}
        />
      ))}
    </Stack>
  );
}

function FollowUpRow({
  followUp,
  onOpenCase,
}: {
  followUp: IndustrialWorkflowOperationsFollowUp;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  const dueTone = getDueTone(
    followUp.next_review_at
      ? getOperationalDueStatus(
          followUp.next_review_at,
        )
      : followUp.attention_status,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${alpha('#f59e0b', 0.16)}`,
        bgcolor: 'rgba(15,23,42,0.42)',
        px: 1.25,
        py: 1.05,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
            {followUp.case.title}
          </Typography>

          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.6 }}>
            <Chip
              size="small"
              label={followUp.stage_name}
              sx={{
                color: '#ddd6fe',
                bgcolor: alpha('#8b5cf6', 0.16),
              }}
            />

            {followUp.tracking_work_area_name ? (
              <Chip
                size="small"
                label={followUp.tracking_work_area_name}
                sx={{
                  color: '#cbd5e1',
                  bgcolor: alpha('#64748b', 0.12),
                }}
              />
            ) : null}

            <Chip
              size="small"
              label={
                followUp.next_review_at
                  ? formatFollowUpDeadlineLabel(
                      followUp.next_review_at,
                    )
                  : `Sin próxima revisión · entrada ${formatRelativeDate(
                      followUp.entered_at,
                    )}`
              }
              sx={dueTone}
            />
          </Stack>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewRounded />}
          onClick={() => onOpenCase(followUp.case)}
          sx={{
            fontWeight: 900,
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Abrir expediente
        </Button>
      </Stack>
    </Paper>
  );
}

function FollowUpsView({
  dashboard,
  onOpenCase,
}: {
  dashboard: IndustrialWorkflowOperationsDashboard;
  onOpenCase: (workflowCase: IndustrialWorkflowCase) => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha('#f59e0b', 0.16)}`,
        bgcolor: 'rgba(2,6,23,0.18)',
        px: 1.35,
        py: 1.25,
      }}
    >
      <Stack spacing={1}>
        <Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
            Seguimientos externos
          </Typography>

          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.2 }}>
            Expedientes detenidos en una etapa de espera externa.
          </Typography>
        </Box>

        {dashboard.follow_ups.length ? (
          dashboard.follow_ups.map((followUp) => (
            <FollowUpRow
              key={followUp.case.id}
              followUp={followUp}
              onOpenCase={onOpenCase}
            />
          ))
        ) : (
          <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
            No hay seguimientos externos pendientes.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export default function IndustrialWorkflowsOperationsDashboard({
  activeView,
  dashboard,
  isLoading,
  isError,
  error,
  assignableUsers,
  onOpenCase,
}: IndustrialWorkflowsOperationsDashboardProps) {
  const assignableUserById = new Map(
    assignableUsers.map((user) => [user.user_id, user]),
  );

  if (isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={18} />

        <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
          Cargando información operativa...
        </Typography>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" variant="outlined">
        {getErrorMessage(error)}
      </Alert>
    );
  }

  if (!dashboard) {
    return null;
  }

  if (dashboard.query) {
    return (
      <Stack spacing={1}>
        <Chip
          size="small"
          icon={<QueryStatsRounded />}
          label={`Resultados filtrados por “${dashboard.query}”`}
          sx={{
            alignSelf: 'flex-start',
            color: '#bae6fd',
            bgcolor: alpha('#0284c7', 0.16),
          }}
        />

        {activeView === 'summary' ? (
          <SummaryView dashboard={dashboard} onOpenCase={onOpenCase} />
        ) : null}

        {activeView === 'my-work' ? (
          <MyWorkView
            dashboard={dashboard}
            assignableUserById={assignableUserById}
            onOpenCase={onOpenCase}
          />
        ) : null}

        {activeView === 'areas' ? (
          <AreasView
            dashboard={dashboard}
            assignableUserById={assignableUserById}
            onOpenCase={onOpenCase}
          />
        ) : null}

        {activeView === 'follow-ups' ? (
          <FollowUpsView dashboard={dashboard} onOpenCase={onOpenCase} />
        ) : null}
      </Stack>
    );
  }

  if (activeView === 'summary') {
    return <SummaryView dashboard={dashboard} onOpenCase={onOpenCase} />;
  }

  if (activeView === 'my-work') {
    return (
      <MyWorkView
        dashboard={dashboard}
        assignableUserById={assignableUserById}
        onOpenCase={onOpenCase}
      />
    );
  }

  if (activeView === 'areas') {
    return (
      <AreasView
        dashboard={dashboard}
        assignableUserById={assignableUserById}
        onOpenCase={onOpenCase}
      />
    );
  }

  return <FollowUpsView dashboard={dashboard} onOpenCase={onOpenCase} />;
}
